import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../utils/apiClient'

export function useNotifications() {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const cursorRef = useRef(null);
    const loadingMoreRef = useRef(false);

    const buildUrl = useCallback((cursor) => {
        const url = new URL('/api/profile/notifications', process.env.REACT_APP_API_URL);
        if (cursor) url.searchParams.append('cursor', cursor);
        return url.toString();
    }, []);

    // Первичная загрузка
    useEffect(() => {
        let isMounted = true;

        const fetchInitial = async () => {
            try {
                setLoading(true);
                setError(null);
                cursorRef.current = null;

                const response = await apiFetch(buildUrl(null), {
                    method: 'GET'
                });

                const data = await response.json();

                if (!response.ok || data.error) {
                    throw new Error(data.error || 'Ошибка получения уведомлений');
                }

                if (isMounted) {
                    setNotifications(data?.notifications ?? []);
                    cursorRef.current = data?.nextCursor ?? null;
                    setHasMore(!!data?.nextCursor);
                }
            } catch (err) {
                if (isMounted) setError(err.message);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchInitial();

        return () => {
            isMounted = false;
        };
    }, [buildUrl]);

    const loadMore = useCallback(async () => {
        if (loadingMoreRef.current || !cursorRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);

        try {
            const response = await apiFetch(buildUrl(cursorRef.current), {
                method: 'GET'
            });

            const data = await response.json();
            if (!response.ok || data.error) throw new Error(data.error ?? 'Ошибка сервера');

            setNotifications(prev => [...(prev ?? []), ...(data.notifications ?? [])]);
            cursorRef.current = data.nextCursor ?? null;
            setHasMore(!!data.nextCursor);
        } catch (err) {
            // Не роняем уже загруженный список — просто прекращаем подгрузку
            setHasMore(false);
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }, [buildUrl]);

    // Локальное удаление (после успешного DELETE на сервере)
    const removeNotification = useCallback((id) => {
        setNotifications(prev => (prev ?? []).filter(item => item.id !== id));
    }, []);

    return { notifications, loading, error, hasMore, loadingMore, loadMore, removeNotification };
}
